import { Dimension } from '@pubstack/common/src/analytics/dimension'
import { AnalyticsQueryDashboard, SparkForPubMetricsByTime } from '@pubstack/common/src/analytics/query'
import { useAtom } from 'jotai'
import { DateTime, Interval } from 'luxon'
import { FunctionComponent } from 'react'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { LazyVisible } from '~/components/LazyVisible'
import { Forbidden } from '~/components/error/Forbidden'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { useIsSparkForPubUiEnabled } from '~/hooks/useSparkForPub'
import { AnalyticsPage } from '~/modules/analytics/AnalyticsPage'
import { contextState } from '~/state'
import { getTimePresets, TIME_RANGE_CONFIG } from '~/utils/analytics'
import { PureSparkForPubTimeline } from './PureSparkForPubTimeline'
import { SparkForPubBreakdown } from './SparkForPubBreakdown'

// The daily tables have no dimension columns: filters set on another dashboard are shown disabled and never sent.
const DIMENSIONS: Dimension[] = []
const DASHBOARD: AnalyticsQueryDashboard = { name: 'spark-for-pub', filterType: 'auctions' }

export const SparkForPubPage: FunctionComponent = () => {
  const sparkForPubUiEnabled = useIsSparkForPubUiEnabled()
  const currencySymbol = useScopeCurrency()
  const [context] = useAtom(contextState)
  const breadcrumbs = useBreadcrumbs()
  const today = DateTime.now().startOf('day')

  const analyticsQueryByTime = useAnalyticsQueryTanstack<SparkForPubMetricsByTime>('spark-for-pub.metrics.by.time', DIMENSIONS, context, {})

  if (!sparkForPubUiEnabled) {
    return <Forbidden />
  }

  return (
    <AnalyticsPage
      onRefreshClick={() => {
        analyticsQueryByTime.refetch()
      }}
      filterDimensions={DIMENSIONS}
      dashboard={DASHBOARD}
      title={breadcrumbs}
      hideFilterSidebar
      // The daily tables are written once the publisher's day is over, so today never has data.
      selectableInterval={Interval.fromDateTimes(today.minus({ days: 93 }), today)}
      timePresets={getTimePresets(today).filter(({ label }) => label !== TIME_RANGE_CONFIG.TODAY.label)}
    >
      <div className="flex flex-col gap-4">
        <PureSparkForPubTimeline
          isLoading={analyticsQueryByTime.isFetching}
          error={!!analyticsQueryByTime.error}
          empty={!analyticsQueryByTime.isLoading && !analyticsQueryByTime?.data?.epoch.length}
          onRefreshClick={() => analyticsQueryByTime.refetch()}
          data={analyticsQueryByTime.data ?? { epoch: [] }}
          currencySymbol={currencySymbol}
          currentEpoch={DateTime.now()}
          timezone={context.timeRange.tz}
          onMetricChange={() => {}}
        />
        <LazyVisible minHeight={400}>
          <SparkForPubBreakdown context={context} dimensions={DIMENSIONS} currencySymbol={currencySymbol} />
        </LazyVisible>
      </div>
    </AnalyticsPage>
  )
}
